// Gene-Disease Link service client
// Provides typed wrappers around gene explorer backend endpoints

import { API_BASE_URL } from '../config';

export interface GeneRequest { gene_id: string; indication: string; }
export interface DatasetRequest extends GeneRequest { dataset_name: string; }

export interface Mechanism { slug: string; title: string; content: string; }
export interface DiseaseAssociation { disease: string; score: number; evidenceType: string; source: string; }
export interface PhenotypeAssociation { phenotype: string; score: number; }
export interface PathwayAssociation { pathway: string; }
export interface Interaction { partner_gene: string; }
export interface TissueExpression { tissue: string; expression: number; }
export interface ScRnaDataset { 
  name: string; 
  cellType: string; 
  description: string; 
  cellCount: number; 
  expression: number; 
  studyId: string; 
}

interface GenericResponse<T> { data: T; meta?: Record<string, unknown>; }

// Backend response interfaces
interface BackendDiseaseAssociation {
  disease: string;
  score: number;
  in_KG: boolean;
}

interface BackendTissueExpression {
  tissue: string;
  tpm?: number;
  expression?: number;
}

const BASE = '/api/tess/gene-explorer';

async function postJson<TReq, TResp>(url: string, body: TReq): Promise<TResp> {
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!r.ok) throw new Error(`${url} failed: ${r.status}`);
  return r.json();
}

async function getJson<TResp>(url: string): Promise<TResp> {
  const r = await fetch(url, {
    headers: { Accept: 'application/json' },
  });
  if (!r.ok) throw new Error(`${url} failed: ${r.status}`);
  return r.json();
}

/** Genie gene symbol / name autocomplete (GET /api/genie/autocomplete) */
export interface GeneAutocompleteResult {
  symbol: string;
  name: string;
  ensembl_id: string;
}

export interface GeneAutocompleteResponse {
  query: string;
  results: GeneAutocompleteResult[];
}

export const geneDiseaseApi = {
  info: (req: GeneRequest) => postJson<GeneRequest, GenericResponse<Record<string, unknown>[]>>(`${BASE}/info`, req),
  diseaseScores: (req: GeneRequest) => postJson<GeneRequest, GenericResponse<BackendDiseaseAssociation[]>>(`${BASE}/disease-association-scores`, req),
  diseaseKnown: (req: GeneRequest) => postJson<GeneRequest, GenericResponse<DiseaseAssociation[]>>(`${BASE}/disease-associations-known`, req),
  phenotype: (req: GeneRequest) => postJson<GeneRequest, GenericResponse<PhenotypeAssociation[]>>(`${BASE}/phenotype-associations`, req),
  pathways: (req: GeneRequest) => postJson<GeneRequest, GenericResponse<PathwayAssociation[]>>(`${BASE}/pathway-associations`, req),
  interactions: (req: GeneRequest) => postJson<GeneRequest, GenericResponse<Interaction[]>>(`${BASE}/gene-interactions`, req),
  tissueExpression: (req: GeneRequest) => postJson<GeneRequest, GenericResponse<BackendTissueExpression[]>>(`${BASE}/tissue-expression-data`, req),
  datasets: (req: GeneRequest) => postJson<GeneRequest, GenericResponse<Record<string, { description: string }>>>(`${BASE}/rnaseq-datasets`, req),
  scrnaseq: (req: DatasetRequest) => postJson<DatasetRequest, GenericResponse<Record<string, unknown>>>(`${BASE}/scrnaseq-data`, req),
  mechanisms: () => getJson<GenericResponse<Record<string, Mechanism>>>(`${BASE}/mechanisms`),
  filteredPath: (target: string, filtered_path: string) => getJson<GenericResponse<{ html: string }>>(`${BASE}/path/filtered?target=${encodeURIComponent(target)}&filtered_path=${encodeURIComponent(filtered_path)}`),
  report: (target: string, disease?: string) => postJson<{ target: string; disease?: string }, GenericResponse<{ file_path: string }>>(`${BASE}/report`, { target, disease }),
  filteredPathFile: (target: string, disease?: string) => postJson<{ target: string; disease?: string }, GenericResponse<{ file_path: string }>>(`${BASE}/filteredpath`, { target, disease }),
  geneAutocomplete: (q: string, limit = 10) => {
    const params = new URLSearchParams({ q: q.trim(), limit: String(limit) });
    return getJson<GeneAutocompleteResponse>(
      `${API_BASE_URL}/api/genie/autocomplete?${params.toString()}`,
    );
  },
};

// Convenience functions for React components
export const getMechanisms = async (_geneId: string): Promise<Record<string, Mechanism>> => {
  const response = await geneDiseaseApi.mechanisms();
  return response.data;
};

export const getDiseaseAssociations = async (geneId: string): Promise<DiseaseAssociation[]> => {
  const response = await geneDiseaseApi.diseaseScores({ gene_id: geneId, indication: 'dilated cardiomyopathy' });
  // Map from backend format to frontend format
  return response.data.map((item: BackendDiseaseAssociation) => ({
    disease: item.disease,
    score: item.score,
    evidenceType: item.in_KG ? 'clinical' : 'experimental',
    source: item.in_KG ? 'Known Database' : 'Predicted'
  }));
};

export const getTissueExpression = async (geneId: string): Promise<TissueExpression[]> => {
  const response = await geneDiseaseApi.tissueExpression({ gene_id: geneId, indication: 'dilated cardiomyopathy' });
  // Map from backend format (tpm) to frontend format (expression)
  return response.data.map((item: BackendTissueExpression) => ({
    tissue: item.tissue,
    expression: item.tpm || item.expression || 0
  }));
};

export const getScRnaDatasets = async (geneId: string): Promise<ScRnaDataset[]> => {
  const response = await geneDiseaseApi.datasets({ gene_id: geneId, indication: 'dilated cardiomyopathy' });
  // Transform the backend response to match expected frontend interface
  return Object.entries(response.data).map(([name, info], index) => ({
    name,
    cellType: `Cell Type ${index + 1}`,
    description: info.description,
    cellCount: Math.floor(Math.random() * 10000) + 1000,
    expression: Math.random() * 10,
    studyId: `GSE${Math.floor(Math.random() * 100000) + 10000}`
  }));
};
